/**
 * Canonical source-resolver utilities for Table column composition.
 *
 * The backend emits source bindings as strings shaped
 * ``{smart_table_id}.{sheet_id}.{column_id}`` (three UUIDs separated by
 * dots). These helpers round-trip that format on the frontend so composition
 * bindings survive rename/move on the upstream side.
 *
 * Keep the pattern in sync with the backend regex
 * ``SMART_TABLE_SOURCE_RESOLVER_PATTERN`` — both accept canonical
 * lowercase UUIDs in the standard 8-4-4-4-12 hex shape.
 */

/**
 * Matches a single canonical lowercase UUID. Mirrors the backend's
 * ``SMART_TABLE_SOURCE_RESOLVER_PATTERN`` segment.
 */
const UUID_SEGMENT = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";

/**
 * Matches a full resolver string: three UUID segments separated by dots.
 * Anchored so partial matches (e.g. a UUID on its own) are rejected.
 */
const RESOLVER_REGEX = new RegExp(`^${UUID_SEGMENT}\\.${UUID_SEGMENT}\\.${UUID_SEGMENT}$`, "i");

export interface ParsedSourceResolver {
  smartTableId: string;
  sheetId: string;
  columnId: string;
}

/**
 * Encode a resolver string for a given (table, sheet, column). No validation
 * is performed — callers are expected to feed UUIDs straight from the
 * discovery endpoint or sheet store.
 */
export const encodeSourceResolver = (
  smartTableId: string,
  sheetId: string,
  columnId: string,
): string => `${smartTableId}.${sheetId}.${columnId}`;

/**
 * Parse a resolver string back into its three UUIDs. Returns ``null`` for
 * anything that isn't shaped like ``uuid.uuid.uuid`` so callers can treat
 * sibling-column titles and resolver strings with a single type guard.
 */
export const parseSourceResolver = (value: string): ParsedSourceResolver | null => {
  if (typeof value !== "string" || !RESOLVER_REGEX.test(value)) {
    return null;
  }

  const parts = value.split(".");
  if (parts.length !== 3) {
    return null;
  }

  const [smartTableId, sheetId, columnId] = parts;
  return { smartTableId, sheetId, columnId };
};
